import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import {
  getTemplate,
  createBuildTemplate,
} from "../../api/services/scheduling";
import { QK } from "../../base/qk";

const BuildTemplateModal = ({ open, onClose, sublocation, date }) => {
  const queryClient = useQueryClient();
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [buildingTemplate, setBuildingTemplate] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const { data: templates, isLoading } = useQuery(
    ["templates", sublocation?.id],
    () => getTemplate(sublocation?.id),
    {
      enabled: !!sublocation?.id,
    }
  );

  const handleBuildTemplate = async (templateId) => {
    setBuildingTemplate(templateId);
    try {
      const response = await createBuildTemplate({
        template: templateId,
        build_at: date,
      });
      if (response.status === 201) {
        queryClient.invalidateQueries(QK.buildHistory);
        enqueueSnackbar("Build successful", { variant: "success" });
        onClose();
      }
    } catch (error) {
      enqueueSnackbar(
        "Build Failed. Check your template if there is any visit available.",
        { variant: "error" }
      );
    } finally {
      setBuildingTemplate(null);
      setSelectedTemplate("");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Build Template
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography sx={{ mb: 2 }}>
          Select template for build at {date}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1,
            mt: 2,
            gap: 2,
          }}
        >
          <Box sx={{ flex: "0 0 33%" }}>
            <Typography sx={{ fontWeight: 500 }}>
              {sublocation?.name}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
            }}
          >
            <TextField
              select
              size="small"
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              disabled={isLoading}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Select Template</MenuItem>
              {templates?.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </TextField>
            {selectedTemplate && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={!!buildingTemplate}
                onClick={() => handleBuildTemplate(selectedTemplate)}
              >
                {buildingTemplate ? "Building..." : "Build"}
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button size="small" variant="outlined" color="inherit" onClick={onClose}>
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BuildTemplateModal;